import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { MessageSquare, Send, CheckCircle, XCircle, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const SMSTestPage = () => {
  const [loading, setLoading] = useState(false);
  const [testPhone, setTestPhone] = useState('');
  const [customMessage, setCustomMessage] = useState('');
  const [results, setResults] = useState([]);

  const addResult = (type, success, message) => {
    setResults(prev => [...prev, { type, success, message, timestamp: new Date() }]);
  };

  const sendTestSMS = async (type, payload) => {
    setLoading(true);
    try {
      const response = await axios.post(`${API_URL}/api/sms/${type}`, payload);
      addResult(type, true, 'SMS sent successfully!');
      toast.success(`${type} SMS sent!`);
    } catch (error) {
      const errorMsg = error.response?.data?.detail || error.message;
      addResult(type, false, errorMsg);
      toast.error(`Failed: ${errorMsg}`);
    } finally {
      setLoading(false);
    }
  };

  const handleCustomSMS = () => {
    if (!testPhone || !customMessage) {
      toast.error('Please enter phone number and message');
      return;
    }
    sendTestSMS('send', { to_phone: testPhone, message: customMessage });
  };

  const handleAppointmentConfirmation = () => {
    if (!testPhone) {
      toast.error('Please enter phone number');
      return;
    }
    sendTestSMS('appointment-confirmation', {
      customer_name: 'Test Customer',
      customer_phone: testPhone,
      barber_name: 'Taine',
      service: 'Haircut & Beard Trim',
      appointment_datetime: 'March 20, 2025 at 2:00 PM'
    });
  };

  const handleAppointmentReminder = () => {
    if (!testPhone) {
      toast.error('Please enter phone number');
      return;
    }
    sendTestSMS('appointment-reminder', {
      customer_name: 'Test Customer',
      customer_phone: testPhone,
      barber_name: 'Taine',
      service: 'Haircut',
      appointment_datetime: 'Tomorrow at 2:00 PM'
    });
  };

  const handlePickNumberWinner = () => {
    if (!testPhone) {
      toast.error('Please enter phone number');
      return;
    }
    sendTestSMS('pick-number-winner', {
      customer_name: 'Test Customer',
      customer_phone: testPhone,
      winning_number: 42
    });
  };

  const handleNoShowAlert = () => {
    if (!testPhone) {
      toast.error('Please enter phone number');
      return;
    }
    sendTestSMS('no-show-alert', {
      customer_name: 'Test Customer',
      customer_phone: testPhone,
      no_show_count: 1
    });
  };

  const handleOnMyWay = () => {
    if (!testPhone) {
      toast.error('Please enter barber phone number');
      return;
    }
    sendTestSMS('on-my-way', {
      barber_name: 'Taine',
      barber_phone: testPhone,
      customer_name: 'Test Customer',
      eta_minutes: 10,
      appointment_time: '2:00 PM'
    });
  };

  const handleMembershipWelcome = () => {
    if (!testPhone) {
      toast.error('Please enter phone number');
      return;
    }
    sendTestSMS('membership-welcome', {
      customer_name: 'Test Customer',
      customer_phone: testPhone,
      membership_tier: 'The Gentleman'
    });
  };

  return (
    <div className="min-h-screen bg-black text-white py-16">
      <div className="container mx-auto px-4">
        {/* Header */}
        <div className="text-center mb-12">
          <div className="flex justify-center mb-4">
            <MessageSquare className="w-16 h-16 text-lime-400" />
          </div>
          <h1 className="text-4xl md:text-5xl font-bold mb-4">
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-lime-400 to-lime-600">
              SMS Testing Dashboard
            </span>
          </h1>
          <p className="text-zinc-400 text-lg">
            Test all Vonage SMS notification types
          </p>
          
          <div className="mt-6">
            <Badge className="bg-purple-600 text-white px-4 py-2 text-base">
              Vonage SMS: Active
            </Badge>
          </div>
        </div>

        {/* Important Notice */}
        <Card className="bg-gradient-to-r from-lime-900/40 to-black border-lime-500 mb-8 max-w-3xl mx-auto">
          <CardContent className="p-6">
            <h3 className="text-xl font-bold text-lime-400 mb-3">✅ SMS Service Active</h3>
            <p className="text-zinc-300 mb-3">
              Your Sauce Sity Barbershop uses Vonage for SMS notifications.
            </p>
            <ol className="list-decimal list-inside space-y-2 text-zinc-300 mb-4">
              <li>All SMS notifications are sent via Vonage</li>
              <li>Enter any phone number in E.164 format (+1XXXXXXXXXX)</li>
              <li>Test different notification types below</li>
              <li>Check your phone for SMS messages</li>
            </ol>
            <p className="text-zinc-500 text-sm">
              SMS notifications include: bookings, reminders, promotions, and alerts.
            </p>
          </CardContent>
        </Card>

        {/* Phone Input */}
        <Card className="bg-zinc-900 border-zinc-800 mb-8 max-w-3xl mx-auto">
          <CardHeader>
            <CardTitle className="text-lime-400">Test Phone Number</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              <Label htmlFor="phone" className="text-zinc-300">
                Enter Phone Number (E.164 format)
              </Label>
              <Input
                id="phone"
                type="tel"
                placeholder="+12396458468"
                value={testPhone}
                onChange={(e) => setTestPhone(e.target.value)}
                className="bg-zinc-800 border-zinc-700 text-white"
              />
              <p className="text-zinc-500 text-sm">
                Must start with + and country code (US: +1)
              </p>
            </div>
          </CardContent>
        </Card>

        {/* Test Buttons Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
          {/* Appointment Confirmation */}
          <Card className="bg-zinc-900 border-zinc-800">
            <CardHeader>
              <CardTitle className="text-lg">Booking Confirmation</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-zinc-400 text-sm mb-4">
                Test appointment confirmation SMS
              </p>
              <Button 
                onClick={handleAppointmentConfirmation}
                disabled={loading || !testPhone}
                className="w-full bg-purple-600 hover:bg-purple-700"
              >
                {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Send className="w-4 h-4 mr-2" />}
                Send Confirmation
              </Button>
            </CardContent>
          </Card>

          {/* Appointment Reminder */}
          <Card className="bg-zinc-900 border-zinc-800">
            <CardHeader>
              <CardTitle className="text-lg">Appointment Reminder</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-zinc-400 text-sm mb-4">
                Test 24-hour reminder SMS
              </p>
              <Button 
                onClick={handleAppointmentReminder}
                disabled={loading || !testPhone}
                className="w-full bg-purple-600 hover:bg-purple-700"
              >
                {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Send className="w-4 h-4 mr-2" />}
                Send Reminder
              </Button>
            </CardContent>
          </Card>

          {/* Pick a Number Winner */}
          <Card className="bg-zinc-900 border-zinc-800">
            <CardHeader>
              <CardTitle className="text-lg">Pick a Number Winner</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-zinc-400 text-sm mb-4">
                Test lottery winner notification
              </p>
              <Button 
                onClick={handlePickNumberWinner}
                disabled={loading || !testPhone}
                className="w-full bg-lime-600 hover:bg-lime-700"
              >
                {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Send className="w-4 h-4 mr-2" />}
                Send Winner Alert
              </Button>
            </CardContent>
          </Card>

          {/* No-Show Alert */}
          <Card className="bg-zinc-900 border-zinc-800">
            <CardHeader>
              <CardTitle className="text-lg">No-Show Alert</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-zinc-400 text-sm mb-4">
                Test missed appointment alert
              </p>
              <Button 
                onClick={handleNoShowAlert}
                disabled={loading || !testPhone}
                className="w-full bg-orange-600 hover:bg-orange-700"
              >
                {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Send className="w-4 h-4 mr-2" />}
                Send No-Show Alert
              </Button>
            </CardContent>
          </Card>

          {/* On My Way */}
          <Card className="bg-zinc-900 border-zinc-800">
            <CardHeader>
              <CardTitle className="text-lg">On My Way (Barber)</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-zinc-400 text-sm mb-4">
                Test client ETA notification
              </p>
              <Button 
                onClick={handleOnMyWay}
                disabled={loading || !testPhone}
                className="w-full bg-blue-600 hover:bg-blue-700"
              >
                {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Send className="w-4 h-4 mr-2" />}
                Send ETA Alert
              </Button>
            </CardContent>
          </Card>

          {/* Membership Welcome */}
          <Card className="bg-zinc-900 border-zinc-800">
            <CardHeader>
              <CardTitle className="text-lg">Membership Welcome</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-zinc-400 text-sm mb-4">
                Test new member welcome SMS
              </p>
              <Button 
                onClick={handleMembershipWelcome}
                disabled={loading || !testPhone}
                className="w-full bg-purple-600 hover:bg-purple-700"
              >
                {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Send className="w-4 h-4 mr-2" />}
                Send Welcome
              </Button>
            </CardContent>
          </Card>
        </div>

        {/* Custom SMS */}
        <Card className="bg-zinc-900 border-zinc-800 mb-8 max-w-3xl mx-auto">
          <CardHeader>
            <CardTitle className="text-lime-400">Custom SMS Message</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label htmlFor="message" className="text-zinc-300">Message</Label>
              <textarea
                id="message"
                rows={4}
                placeholder="Enter your custom message..."
                value={customMessage}
                onChange={(e) => setCustomMessage(e.target.value)}
                className="w-full bg-zinc-800 border-zinc-700 text-white rounded-md p-3"
              />
            </div>
            <Button 
              onClick={handleCustomSMS}
              disabled={loading || !testPhone || !customMessage}
              className="w-full bg-gradient-to-r from-lime-500 to-lime-600 text-black hover:from-lime-600 hover:to-lime-700"
            >
              {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Send className="w-4 h-4 mr-2" />}
              Send Custom SMS
            </Button>
          </CardContent>
        </Card>

        {/* Results Log */}
        {results.length > 0 && (
          <Card className="bg-zinc-900 border-zinc-800 max-w-3xl mx-auto">
            <CardHeader>
              <CardTitle className="text-lime-400">Test Results</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {results.slice().reverse().map((result, idx) => (
                  <div key={idx} className="flex items-start gap-3 p-3 bg-zinc-800 rounded-lg">
                    {result.success ? (
                      <CheckCircle className="w-5 h-5 text-lime-400 flex-shrink-0 mt-0.5" />
                    ) : (
                      <XCircle className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" />
                    )}
                    <div className="flex-1">
                      <p className={`font-semibold ${result.success ? 'text-lime-400' : 'text-red-400'}`}>
                        {result.type}
                      </p>
                      <p className="text-zinc-300 text-sm">{result.message}</p>
                      <p className="text-zinc-500 text-xs mt-1">
                        {result.timestamp.toLocaleTimeString()}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
};

export default SMSTestPage;
